import React from "react";
import PropTypes from "prop-types";
import classNames from "classnames";
import {observer, inject} from "mobx-react";
import {Container, Col, Row} from "react-bootstrap";
import {Redirect} from "react-router-dom";

import copy from "./copy";
import { isE2ETest } from '../../../prequal/about-you/about-you-helpers';
import {TextOnlyHeader} from "../../../components/text-value-prop-header";
import {FancySpinner} from "../../../components/spinners/fancy-spinner";
import {VehicleFinancingUnavailable} from "../../../components/vehicle-financing-unavailable";
import {Captcha} from "../../../components/captcha";
import {DisclosureBlock} from "../../../components/disclosure-block";
import {isInIFrame, postMessageScrollIntoView} from "../../../utils/iframe-utils";
import {CampingWorldPrequalStepStore} from "./camping-world-prequal-step-store";
import {ServerError} from "../../../components/interstitials";
import {
   CityField,
   DateOfBirthField,
   EmailField,
   EmployerDateField,
   EmployerNameField,
   EmployerPhoneNumberField,
   Street1Field,
   Street2Field,
   YearlyIncomeField,
   JobTitleField,
   MonthlyHousingPaymentField,
   PhoneNumberField,
   SSNField,
   StateField,
   ZipcodeField,
   EmploymentStatusField,
   CitizenshipStatusField,
   ResidentialStatusField,
   ContactPreferenceField,
   FirstNameField,
   MiddleNameField,
   LastNameField,
   SubmitFormButton,
   CoapplicantIntentField,
   RelationshipToPrimaryField,
   ReusePrimaryApplicantAddressField, IncomeField, ResidentialDateField, NumberField,
} from "../../../form-fields";
import { logVehicleFinancingUnavailable } from "../../../prequal/about-you/about-you-helpers";
import {SectionHeader} from "../../../components/section-header";
import {SparkLabelFontOverride} from "./camping-world-prequal-step.styled";

export const createCustomerEventHandler = (store, removeEventListener) => {
  const customerListenerTimeout = setTimeout(() => {
    removeEventListener();
  }, 6000);

  const customerEventHandler = (event) => {
    if (typeof event.data === 'object' && event.data !== null) {
      if ('customer' in event.data) {
        store.replaceFieldValues(event.data.customer);
        clearTimeout(customerListenerTimeout);
        removeEventListener();
      }
    }
  };

  return customerEventHandler;
};

export const CampingWorldPrequalStep = inject(
    "apiStore",
    "uxCopyStore",
    "partnerStore",
    "dealershipStore",
    "themeStore",
    "historyStore",
    "rideOctaneStore",
    "waffleStore",
    "flowStore",
    "userStore",
)(
    observer(
        class _CampingWorldPrequalStep extends React.Component {
            static propTypes = {
                accessoriesAmount: PropTypes.number,
                hasFetchedVehicle: PropTypes.bool,
                vehicleConfiguration: PropTypes.shape({
                    name: PropTypes.string.isRequired,
                    msrp: PropTypes.number.isRequired,
                    default_image: PropTypes.string.isRequired,
                }),
                isDummyVehicleEnabled: PropTypes.bool.isRequired,
                rawVehicleData: PropTypes.object,
                isCoapplicantFlow: PropTypes.bool,
            };

            captchaRef = React.createRef();
            store = new CampingWorldPrequalStepStore(this.props);

            componentDidMount() {
               window.scrollTo(0, 0);
               if (this.captchaRef && this.captchaRef.current) {
                  this.captchaRef.current.reset();
                }

                this.customerEventHandler = createCustomerEventHandler(
                  this.store,
                  () => window.removeEventListener('message', this.customerEventHandler)
                );

                window.addEventListener('message', this.customerEventHandler)


            }

            launchCaptcha = () => {
                const email = this.store.fieldStates.email.value;
                if (isInIFrame()) {
                    postMessageScrollIntoView(window.top);
                } else {
                    window.scrollTo(0, 0);
                }
                // Validation for e2e tests to bypass captcha
                if (isE2ETest(this.store.rideOctaneStore.settings.ENVIRONMENT_KEY, email)) {
                    this.store.submitForm(
                        null,
                        this.props.vehicleConfiguration,
                        this.props.rawVehicleData,
                        this.props.flowStore.updateApplicationUuid,
                        this.props.flowStore.updateApplicantUuids,
                    )
                } else {
                    this.captchaRef.current.execute();
                }
            };

            render() {
                if(this.store.showServerError) {
                    return (
                        <Container data-oid="camping-world-prequal-step-error-page">
                            <ServerError origin="camping-world-prequal-step" errorMessage={this.store.serverErrorMessage}/>
                        </Container>
                    );
                }

                // If haven't fetched the vehicle and we need the vehicle for the VehicleHeader display the fancy loading spinner
                if (this.store.headerType === "VehicleHeader" && !this.props.hasFetchedVehicle){
                    return (
                        <Container data-oid="camping-world-prequal-loading">
                            <FancySpinner className="d-block mx-auto my-5" />
                        </Container>
                    );
                }

                // If the vehicle has been fetched and there is no vehicle configuration and this partner doesn't decision on dummmy vehicles
                if (this.props.hasFetchedVehicle && !this.props.vehicleConfiguration && !this.props.isDummyVehicleEnabled) {
                    if (!this.store.showVehicleFinancingUnavailable) {
                        return <Redirect to="/" />;
                    }
                    logVehicleFinancingUnavailable(
                        this.props.partnerStore.partnerIdentifier,
                        this.props.hasFetchedVehicle,
                        this.props.vehicleConfiguration,
                        this.props.isDummyVehicleEnabled
                        );
                    return (
                        <Container data-oid="camping-world-prequal-unavailable">
                            <VehicleFinancingUnavailable />
                        </Container>
                    );
                }

                return (
                    <Container data-oid="camping-world-prequal-container" key="primary_applicant_flow">
                        <div className={classNames("w-450px mw-100 mx-auto")}>
                           <SparkLabelFontOverride>
                                {!this.props.isCoapplicantFlow ? (
                                    <TextOnlyHeader
                                        className="my-3"
                                        title="Get Prequalified."
                                        subtitle="Get a personalized decision without impacting your credit."
                                    />
                                ) : (
                                    <TextOnlyHeader
                                        className="my-3"
                                        title="Add your co-applicant"
                                    />
                                )}

                            <div id="camping-world-prequal-form">
                                {this.props.isCoapplicantFlow &&
                                    <RelationshipToPrimaryField
                                        controlId={"relationship_to_primary"}
                                        value={this.store.fieldStates["relationship_to_primary"].value}
                                        error={this.store.fieldStates["relationship_to_primary"].error}
                                        onChange={(event) => this.store.updateFieldValue(event, "relationship_to_primary")}
                                    />
                                }

                                  <FirstNameField
                                      controlId={"first_name"}
                                      value={this.store.fieldStates["first_name"].value}
                                      error={this.store.fieldStates["first_name"].error}
                                      onChange={(event) => this.store.updateFieldValue(event, "first_name")}
                                  />
                                 <MiddleNameField
                                      controlId={"middle_name"}
                                      value={this.store.fieldStates["middle_name"].value}
                                      error={this.store.fieldStates["middle_name"].error}
                                      onChange={(event) => this.store.updateFieldValue(event, "middle_name")}
                                  />
                                  <LastNameField
                                      controlId={"last_name"}
                                      value={this.store.fieldStates["last_name"].value}
                                      error={this.store.fieldStates["last_name"].error}
                                      onChange={(event) => this.store.updateFieldValue(event, "last_name")}
                                  />

                                <EmailField
                                    controlId={"email"}
                                    label={copy.email_label}
                                    value={this.store.fieldStates["email"].value}
                                    error={this.store.fieldStates["email"].error}
                                    onChange={(event) => this.store.updateFieldValue(event, "email")}
                                />

                               {!this.props.isCoapplicantFlow &&
                                    <ContactPreferenceField
                                        controlId={"contact_preference"}
                                        value={this.store.fieldStates["contact_preference"].value}
                                        error={this.store.fieldStates["contact_preference"].error}
                                        onChange={(event) => this.store.updateFieldValue(event, "contact_preference")}
                                        partnerStore={this.store.partnerStore}
                                    />
                                }

                                <PhoneNumberField
                                    controlId={"phone_number"}
                                    label={copy.primary_phone_label}
                                    placeholder={copy.standard_phone_placeholder}
                                    value={this.store.fieldStates["phone_number"].value}
                                    error={this.store.fieldStates["phone_number"].error}
                                    onChange={(event) => this.store.updateFieldValue(event, "phone_number")}
                                />

                               <PhoneNumberField
                                    controlId={"cell_phone_number"}
                                    label={copy.cell_phone_label}
                                    placeholder={""}
                                    value={this.store.fieldStates["cell_phone_number"].value}
                                    error={this.store.fieldStates["cell_phone_number"].error}
                                    onChange={(event) => this.store.updateFieldValue(event, "cell_phone_number")}
                                />

                                {this.props.isCoapplicantFlow && this.store.primaryApplicantAddressData &&
                                    <ReusePrimaryApplicantAddressField
                                        controlId={"reuse_primary_address"}
                                        value={this.store.fieldStates["reuse_primary_address"].value}
                                        error={this.store.fieldStates["reuse_primary_address"].error}
                                        onChange={(event) => this.store.onReusePrimaryAddressChange(event)}
                                    />
                                }

                                <Street1Field
                                    controlId={"street1"}
                                    value={this.store.fieldStates["street1"].value}
                                    error={this.store.fieldStates["street1"].error}
                                    onChange={(event) => this.store.updateFieldValue(event, "street1")}
                                    required={!this.store.isReusingPrimaryAddress}
                                    disabled={this.store.isReusingPrimaryAddress}
                                />

                                <Street2Field
                                    controlId={"street2"}
                                    label={copy.address_2_label}
                                    value={this.store.fieldStates["street2"].value}
                                    error={this.store.fieldStates["street2"].error}
                                    onChange={(event) => this.store.updateFieldValue(event, "street2")}
                                    disabled={this.store.isReusingPrimaryAddress}
                                />

                                <CityField
                                    controlId={"city"}
                                    value={this.store.fieldStates["city"].value}
                                    error={this.store.fieldStates["city"].error}
                                    onChange={(event) => this.store.updateFieldValue(event, "city")}
                                    required={!this.store.isReusingPrimaryAddress}
                                    disabled={this.store.isReusingPrimaryAddress}
                                />

                                <Row>
                                    <Col xs={6}>
                                        <StateField
                                            controlId={"state"}
                                            error={this.store.fieldStates["state"].error}
                                            value={this.store.fieldStates["state"].value}
                                            onChange={(event) => this.store.updateFieldValue(event, "state")}
                                            required={!this.store.isReusingPrimaryAddress}
                                            disabled={this.store.isReusingPrimaryAddress}
                                        />
                                    </Col>
                                    <Col xs={6}>
                                        <ZipcodeField
                                            controlId={"zip_code"}
                                            value={this.store.fieldStates["zip_code"].value}
                                            error={this.store.fieldStates["zip_code"].error}
                                            onChange={(event) => this.store.updateFieldValue(event, "zip_code")}
                                            required={!this.store.isReusingPrimaryAddress}
                                            disabled={this.store.isReusingPrimaryAddress}
                                        />
                                    </Col>
                                </Row>

                               <ResidentialStatusField
                                  controlId={"residential_status"}
                                  label={copy.residence_field_label}
                                  value={this.store.fieldStates["residential_status"].value}
                                  error={this.store.fieldStates["residential_status"].error}
                                  onChange={(event) => this.store.updateFieldValue(event, "residential_status")}
                              />

                               <MonthlyHousingPaymentField
                                    controlId={"monthly_housing_payment"}
                                    value={this.store.fieldStates["monthly_housing_payment"].value}
                                    error={this.store.fieldStates["monthly_housing_payment"].error}
                                    onChange={(event) => this.store.updateFieldValue(event, "monthly_housing_payment")}
                               />

                               <ResidentialDateField
                                    controlId={"residential_date"}
                                    value={this.store.fieldStates["residential_date"].value}
                                    error={this.store.fieldStates["residential_date"].error}
                                    onChange={(event) => this.store.updateFieldValue(event, "residential_date")}
                                />

                               {this.store.hasLivedHereForLessThanTwoYears && (
                                   <React.Fragment>
                                       <Street1Field
                                            controlId={"previous_street1"}
                                            label={copy.previous_address_label}
                                            value={this.store.fieldStates["previous_street1"].value}
                                            error={this.store.fieldStates["previous_street1"].error}
                                            onChange={(event) => this.store.updateFieldValue(event, "previous_street1")}
                                            required={this.store.hasLivedHereForLessThanTwoYears}
                                            disabled={!this.store.hasLivedHereForLessThanTwoYears}
                                        />

                                        <Street2Field
                                            controlId={"previous_street2"}
                                            label={copy.previous_address_2_label}
                                            value={this.store.fieldStates["previous_street2"].value}
                                            error={this.store.fieldStates["previous_street2"].error}
                                            onChange={(event) => this.store.updateFieldValue(event, "previous_street2")}
                                            required={this.store.hasLivedHereForLessThanTwoYears}
                                            disabled={!this.store.hasLivedHereForLessThanTwoYears}
                                        />

                                        <CityField
                                            controlId={"previous_city"}
                                            label={copy.previous_city}
                                            value={this.store.fieldStates["previous_city"].value}
                                            error={this.store.fieldStates["previous_city"].error}
                                            onChange={(event) => this.store.updateFieldValue(event, "previous_city")}
                                            required={this.store.hasLivedHereForLessThanTwoYears}
                                            disabled={!this.store.hasLivedHereForLessThanTwoYears}
                                        />

                                        <Row>
                                            <Col xs={6}>
                                                <StateField
                                                    controlId={"previous_state"}
                                                    label={copy.previous_state}
                                                    error={this.store.fieldStates["previous_state"].error}
                                                    value={this.store.fieldStates["previous_state"].value}
                                                    onChange={(event) => this.store.updateFieldValue(event, "previous_state")}
                                                    required={this.store.hasLivedHereForLessThanTwoYears}
                                                    disabled={!this.store.hasLivedHereForLessThanTwoYears}
                                                />
                                            </Col>
                                            <Col xs={6}>
                                                <ZipcodeField
                                                    controlId={"previous_zip_code"}
                                                    label={copy.previous_zip_code}
                                                    value={this.store.fieldStates["previous_zip_code"].value}
                                                    error={this.store.fieldStates["previous_zip_code"].error}
                                                    onChange={(event) => this.store.updateFieldValue(event, "previous_zip_code")}
                                                    required={this.store.hasLivedHereForLessThanTwoYears}
                                                    disabled={!this.store.hasLivedHereForLessThanTwoYears}
                                                />
                                            </Col>
                                        </Row>
                                      <Row>
                                          <Col xs={6}>
                                              <NumberField
                                                  controlId={"previous_residential_years"}
                                                  label={copy.generic_years}
                                                  value={this.store.fieldStates["previous_residential_years"].value}
                                                  error={this.store.fieldStates["previous_residential_years"].error}
                                                  onChange={(event) => this.store.updateFieldValue(event, "previous_residential_years")}
                                                  required={this.store.hasLivedHereForLessThanTwoYears}
                                                  disabled={!this.store.hasLivedHereForLessThanTwoYears}
                                              />
                                          </Col>
                                          <Col xs={6}>
                                              <NumberField
                                                  controlId={"previous_residential_months"}
                                                  label={copy.generic_months}
                                                  value={this.store.fieldStates["previous_residential_months"].value}
                                                  error={this.store.fieldStates["previous_residential_months"].error}
                                                  onChange={(event) => this.store.updateFieldValue(event, "previous_residential_months")}
                                                  required={this.store.hasLivedHereForLessThanTwoYears}
                                                  disabled={!this.store.hasLivedHereForLessThanTwoYears}
                                              />
                                          </Col>
                                      </Row>
                                   </React.Fragment>
                               )}

                                <DateOfBirthField
                                    controlId={"date_of_birth"}
                                    value={this.store.fieldStates["date_of_birth"].value}
                                    error={this.store.fieldStates["date_of_birth"].error}
                                    onChange={(event) => this.store.updateFieldValue(event, "date_of_birth")}
                                />

                                 <SSNField
                                    controlId={"social_security_number"}
                                    value={this.store.fieldStates["social_security_number"].value}
                                    error={this.store.fieldStates["social_security_number"].error}
                                    onChange={(event) => this.store.updateFieldValue(event, "social_security_number")}
                                />

                                <CitizenshipStatusField
                                    controlId={"citizenship_status"}
                                    label={copy.citizenship_status_label}
                                    value={this.store.fieldStates["citizenship_status"].value}
                                    error={this.store.fieldStates["citizenship_status"].error}
                                    onChange={(event) => this.store.updateFieldValue(event, "citizenship_status")}
                                />

                               <SectionHeader
                                    controlId={"section_header_incoming_employment"}
                                    title={copy.section_header_employment_income}
                               />

                               <EmploymentStatusField
                                    controlId={"employment_status"}
                                    value={this.store.fieldStates["employment_status"].value}
                                    error={this.store.fieldStates["employment_status"].error}
                                    onChange={(event) => this.store.updateFieldValue(event, "employment_status")}
                                />

                                <EmployerNameField
                                    controlId={"employer_name"}
                                    value={this.store.fieldStates["employer_name"].value}
                                    error={this.store.fieldStates["employer_name"].error}
                                    onChange={(event) => this.store.updateFieldValue(event, "employer_name")}
                                />

                                <EmployerPhoneNumberField
                                    controlId={"employer_phone"}
                                    placeholder={copy.standard_phone_placeholder}
                                    value={this.store.fieldStates["employer_phone"].value}
                                    error={this.store.fieldStates["employer_phone"].error}
                                    onChange={(event) => this.store.updateFieldValue(event, "employer_phone")}
                                />

                               <JobTitleField
                                    controlId={"job_title"}
                                    label={copy.job_title_label}
                                    value={this.store.fieldStates["job_title"].value}
                                    error={this.store.fieldStates["job_title"].error}
                                    onChange={(event) => this.store.updateFieldValue(event, "job_title")}
                                />

                               <EmployerDateField
                                    controlId={"employer_date"}
                                    value={this.store.fieldStates["employer_date"].value}
                                    error={this.store.fieldStates["employer_date"].error}
                                    onChange={(event) => this.store.updateFieldValue(event, "employer_date")}
                                />

                               {this.store.hasWorkedHereForLessThanTwoYears && (
                                   <React.Fragment>
                                       <EmployerNameField
                                          controlId={"previous_employer_name"}
                                          label={copy.previous_employer_name_label}
                                          value={this.store.fieldStates["previous_employer_name"].value}
                                          error={this.store.fieldStates["previous_employer_name"].error}
                                          onChange={(event) => this.store.updateFieldValue(event, "previous_employer_name")}
                                          required={this.store.hasWorkedHereForLessThanTwoYears}
                                          disabled={!this.store.hasWorkedHereForLessThanTwoYears}
                                      />

                                      <Row>
                                          <Col xs={6}>
                                              <NumberField
                                                  controlId={"previous_employer_years"}
                                                  label={copy.generic_years}
                                                  value={this.store.fieldStates["previous_employer_years"].value}
                                                  error={this.store.fieldStates["previous_employer_years"].error}
                                                  onChange={(event) => this.store.updateFieldValue(event, "previous_employer_years")}
                                                  required={this.store.hasWorkedHereForLessThanTwoYears}
                                                  disabled={!this.store.hasWorkedHereForLessThanTwoYears}
                                              />
                                          </Col>
                                          <Col xs={6}>
                                              <NumberField
                                                  controlId={"previous_employer_months"}
                                                  label={copy.generic_months}
                                                  value={this.store.fieldStates["previous_employer_months"].value}
                                                  error={this.store.fieldStates["previous_employer_months"].error}
                                                  onChange={(event) => this.store.updateFieldValue(event, "previous_employer_months")}
                                                  required={this.store.hasWorkedHereForLessThanTwoYears}
                                                  disabled={!this.store.hasWorkedHereForLessThanTwoYears}
                                              />
                                          </Col>
                                      </Row>

                                      <JobTitleField
                                          controlId={"previous_job_title"}
                                          label={copy.previous_job_title_label}
                                          value={this.store.fieldStates["previous_job_title"].value}
                                          error={this.store.fieldStates["previous_job_title"].error}
                                          onChange={(event) => this.store.updateFieldValue(event, "previous_job_title")}
                                          required={this.store.hasWorkedHereForLessThanTwoYears}
                                          disabled={!this.store.hasWorkedHereForLessThanTwoYears}
                                      />

                                      <EmployerPhoneNumberField
                                          controlId={"previous_employer_phone"}
                                          placeholder={copy.standard_phone_placeholder}
                                          value={this.store.fieldStates["previous_employer_phone"].value}
                                          error={this.store.fieldStates["previous_employer_phone"].error}
                                          onChange={(event) => this.store.updateFieldValue(event, "previous_employer_phone")}
                                          required={this.store.hasWorkedHereForLessThanTwoYears}
                                          disabled={!this.store.hasWorkedHereForLessThanTwoYears}
                                      />
                                   </React.Fragment>
                               )}

                              <YearlyIncomeField
                                    controlId={"yearly_income"}
                                    placeholder={""}
                                    label={copy.yearly_income_label}
                                    hintLabelText={copy.yearly_income_hint_label}
                                    value={this.store.fieldStates["yearly_income"].value}
                                    error={this.store.fieldStates["yearly_income"].error}
                                    onChange={(event) => this.store.updateFieldValue(event, "yearly_income")}
                                />

                               <IncomeField
                                    controlId={"income_social_security"}
                                    label={copy.income_social_security_label}
                                    hintLabelText={copy.optional_income_hint_label}
                                    value={this.store.fieldStates["income_social_security"].value}
                                    error={this.store.fieldStates["income_social_security"].error}
                                    onChange={(event) => this.store.updateFieldValue(event, "income_social_security")}
                                />

                               <IncomeField
                                    controlId={"income_retirement"}
                                    label={copy.income_pension_retirement_label}
                                    hintLabelText={copy.optional_income_hint_label}
                                    value={this.store.fieldStates["income_retirement"].value}
                                    error={this.store.fieldStates["income_retirement"].error}
                                    onChange={(event) => this.store.updateFieldValue(event, "income_retirement")}
                                />

                               <IncomeField
                                    controlId={"income_rental"}
                                    label={copy.income_rental_label}
                                    hintLabelText={copy.optional_income_hint_label}
                                    value={this.store.fieldStates["income_rental"].value}
                                    error={this.store.fieldStates["income_rental"].error}
                                    onChange={(event) => this.store.updateFieldValue(event, "income_rental")}
                                />

                               <IncomeField
                                    controlId={"income_child_support"}
                                    label={copy.income_child_support_label}
                                    hintLabelText={copy.optional_income_hint_label}
                                    value={this.store.fieldStates["income_child_support"].value}
                                    error={this.store.fieldStates["income_child_support"].error}
                                    onChange={(event) => this.store.updateFieldValue(event, "income_child_support")}
                                />

                               <IncomeField
                                    controlId={"income_other"}
                                    label={copy.income_other_label}
                                    hintLabelText={copy.optional_income_hint_label}
                                    value={this.store.fieldStates["income_other"].value}
                                    error={this.store.fieldStates["income_other"].error}
                                    onChange={(event) => this.store.updateFieldValue(event, "income_other")}
                                />

                                {this.store.showCoapplicantIntentField &&
                                    <CoapplicantIntentField
                                        controlId={"coapplicant_intent"}
                                        value={this.store.fieldStates["coapplicant_intent"].value}
                                        error={this.store.fieldStates["coapplicant_intent"].error}
                                        onChange={(event) => this.store.updateFieldValue(event, "coapplicant_intent")}
                                    />
                                }

                                <div>
                                    <DisclosureBlock
                                        id="consent"
                                        showCheckBox
                                        showRuleLine
                                        disclosureText={this.store.prequalDisclosure}
                                        onChange={this.store.updatePrequalDisclosureConsent}
                                        isChecked={this.store.prequalDisclosureConsent || false}
                                    />
                                    <DisclosureBlock
                                        id="text-consent"
                                        showCheckBox
                                        disclosureText={copy.text_consent.labelContent}
                                        onChange={this.store.updateTextConsent}
                                        isChecked={this.store.textConsent || false}
                                    />

                                    <Captcha
                                        captchaRef={this.captchaRef}
                                        onClickContinue={(captcha) =>
                                        this.store.submitForm(
                                            captcha,
                                            this.props.vehicleConfiguration,
                                            this.props.rawVehicleData,
                                            this.props.flowStore.updateApplicationUuid,
                                            this.props.flowStore.updateApplicantUuids,
                                        )}
                                    />

                                    <SubmitFormButton
                                        canContinue={
                                            this.props.hasFetchedVehicle
                                            && this.store.prequalDisclosureConsent
                                            && this.store.isFormValid
                                            && this.store.textConsent
                                        }
                                        oid='cta-camping-world-prequal'
                                        onClick={this.launchCaptcha}
                                        continueButtonLabel={this.store.continueButtonLabel}
                                        partnerStore={this.store.partnerStore}
                                    />
                                </div>
                            </div>
                           </SparkLabelFontOverride>
                        </div>
                    </Container>
                );
            }
        },
    ),
);
